import React, { useEffect } from 'react';
import { useHolidayTheme } from '../../../contexts/holiday-theme-context';
import './holiday-theme.css';

// Holiday colors
const CHRISTMAS_COLORS = {
  primary: '#C8102E', // Red
  secondary: '#228B22', // Green
  accent: '#FFD700', // Gold
  light: '#FFE4E1', // Misty Rose
};

const NEWYEAR_COLORS = {
  primary: '#FFD700', // Gold
  secondary: '#C0C0C0', // Silver
  accent: '#1E90FF', // Blue
  light: '#FFF8DC', // Cornsilk
};

export const HolidayTheme: React.FC = () => {
  const { isHolidaySeason, holidayType, enableMusic, enableAnimations } = useHolidayTheme();

  // Apply holiday colors to CSS variables
  useEffect(() => {
    if (!isHolidaySeason || !holidayType) {
      document.documentElement.style.removeProperty('--holiday-primary');
      document.documentElement.style.removeProperty('--holiday-secondary');
      document.documentElement.style.removeProperty('--holiday-accent');
      document.documentElement.style.removeProperty('--holiday-light');
      return;
    }

    const colors = holidayType === 'christmas' ? CHRISTMAS_COLORS : NEWYEAR_COLORS;
    document.documentElement.style.setProperty('--holiday-primary', colors.primary);
    document.documentElement.style.setProperty('--holiday-secondary', colors.secondary);
    document.documentElement.style.setProperty('--holiday-accent', colors.accent);
    document.documentElement.style.setProperty('--holiday-light', colors.light);
    document.documentElement.classList.add('holiday-theme-active');
    document.documentElement.classList.add(`holiday-${holidayType}`);

    return () => {
      document.documentElement.classList.remove('holiday-theme-active');
      document.documentElement.classList.remove(`holiday-${holidayType}`);
    };
  }, [isHolidaySeason, holidayType]);

  // Background music (optional, muted by default)
  // Note: Audio can be added later by importing Howl and loading audio files
  useEffect(() => {
    if (!isHolidaySeason || !enableMusic) {
      return;
    }

    // Placeholder for future audio implementation
    // Example:
    // const sound = new Howl({ src: ['/audio/holiday-music.mp3'], loop: true, volume: 0.3 });
    // sound.play();
    // musicRef.current = sound;
    // return () => sound.stop();
  }, [isHolidaySeason, enableMusic]);

  if (!isHolidaySeason) {
    return null;
  }

  return (
    <>
      {enableAnimations && (
        <>
          {holidayType === 'christmas' && (
            <div className="snowflakes" aria-hidden="true">
              {Array.from({ length: 40 }).map((_, i) => {
                const randomLeft = Math.random() * 100;
                const randomDuration = 8 + Math.random() * 4; // 8-12 seconds (faster)
                const randomDelay = Math.random() * 5;
                const randomDrift = (Math.random() - 0.5) * 60; // -30px to 30px (more drift)
                const randomSize = 0.8 + Math.random() * 0.6; // 0.8-1.4em (size variation)
                // Vary the color slightly for more visual interest
                const colorVariation = 0.85 + Math.random() * 0.15; // 0.85 to 1.0
                const blueTint = Math.floor(176 * colorVariation);
                const greenTint = Math.floor(224 * colorVariation);
                const redTint = Math.floor(255 * colorVariation);
                // Lighter opacity for less dense appearance
                const opacity = 0.5 + Math.random() * 0.3; // 0.5 to 0.8
                return (
                  <div
                    key={i}
                    className="snowflake"
                    style={{
                      left: `${randomLeft}%`,
                      animationDuration: `${randomDuration}s`,
                      animationDelay: `${randomDelay}s`,
                      fontSize: `${randomSize}em`,
                      color: `rgba(${blueTint}, ${greenTint}, ${redTint}, ${opacity})`,
                      '--drift': `${randomDrift}px`,
                    } as React.CSSProperties}
                  >
                    ❄
                  </div>
                );
              })}
            </div>
          )}
          {holidayType === 'newyear' && (
            <div className="sparkles" aria-hidden="true">
              {Array.from({ length: 30 }).map((_, i) => {
                const randomLeft = Math.random() * 100;
                const randomTop = Math.random() * 100;
                const randomDuration = 3 + Math.random() * 2; // 3-5 seconds
                const randomDelay = Math.random() * 2;
                return (
                  <div
                    key={i}
                    className="sparkle"
                    style={{
                      left: `${randomLeft}%`,
                      top: `${randomTop}%`,
                      animationDuration: `${randomDuration}s`,
                      animationDelay: `${randomDelay}s`,
                    }}
                  >
                    ✨
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
};

